import { createTheme, type Theme } from '@mui/material';
import { APP_FONT_FAMILY } from './fonts';
import { isHelpRtl } from '../i18n/help';

/**
 * Help content shares the exact same unified multi-script font stack as the rest
 * of the platform, so Latin and Arabic (and any other script) render identically.
 */
export const HELP_FONT_FAMILY = APP_FONT_FAMILY;

export const HELP_FONT_FAMILY_EN = HELP_FONT_FAMILY;

export const HELP_FONT_FAMILY_AR = HELP_FONT_FAMILY;

export const getHelpFontFamily = (_language: string): string => HELP_FONT_FAMILY;

/** Weights mirror the platform exactly across languages (Noto Sans pairs 1:1 with Readex Pro). */
export const getHelpFontWeight = (
  _language: string,
  weight: 400 | 500 | 600
): 400 | 500 | 600 => weight;

export const HELP_PAGE_TITLE_SX = {
  fontSize: '2.125rem',
  lineHeight: 1.235,
  letterSpacing: 0,
} as const;

export const HELP_KNOWLEDGE_CARD_TITLE_SX = {
  fontSize: '1rem',
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

export const HELP_KNOWLEDGE_CARD_TITLE_MIN_HEIGHT = '2.9rem';

export const HELP_KNOWLEDGE_CARD_LINK_SX = {
  fontSize: '0.8125rem',
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

export const HELP_BODY_SX = {
  fontSize: '0.9375rem',
  lineHeight: 1.75,
  letterSpacing: 0,
} as const;

/** Card / section / step headings inside an article. */
export const HELP_CONTENT_TITLE_SX = {
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

/** Inline option / list-item labels (key terms). */
export const HELP_CONTENT_LABEL_SX = {
  fontSize: '0.9375rem',
  fontWeight: 600,
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

/** Primary running text (paragraphs, step bodies, bullets). */
export const HELP_CONTENT_BODY_SX = {
  fontSize: '0.9375rem',
  fontWeight: 400,
  lineHeight: 1.75,
  letterSpacing: 0,
} as const;

/** Secondary / supporting descriptions. */
export const HELP_CONTENT_DESCRIPTION_SX = {
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.6,
  letterSpacing: 0,
} as const;

/** Numbered step badges. */
export const HELP_CONTENT_STEP_BADGE_SX = {
  fontSize: '0.8125rem',
  fontWeight: 700,
  lineHeight: 1,
} as const;

export const helpTextRenderingSx = {
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  textRendering: 'optimizeLegibility',
} as const;

export const getKnowledgeCardTitleSx = (language: string) => ({
  ...HELP_KNOWLEDGE_CARD_TITLE_SX,
  ...helpTextRenderingSx,
  fontFamily: getHelpFontFamily(language),
  fontWeight: getHelpFontWeight(language, 600),
  minHeight: HELP_KNOWLEDGE_CARD_TITLE_MIN_HEIGHT,
});

export const getKnowledgeCardLinkSx = (language: string) => ({
  ...HELP_KNOWLEDGE_CARD_LINK_SX,
  ...helpTextRenderingSx,
  fontFamily: getHelpFontFamily(language),
  fontWeight: getHelpFontWeight(language, 600),
});

const withHelpFontFamily = (parentTheme: Theme, language: string) => {
  const fontFamily = getHelpFontFamily(language);
  const { typography } = parentTheme;

  return {
    ...typography,
    fontFamily,
    h4: { ...typography.h4, fontFamily },
    h6: { ...typography.h6, fontFamily },
    subtitle1: { ...typography.subtitle1, fontFamily },
    subtitle2: { ...typography.subtitle2, fontFamily },
    body1: { ...typography.body1, fontFamily },
    body2: { ...typography.body2, fontFamily },
    caption: { ...typography.caption, fontFamily },
    button: { ...typography.button, fontFamily },
  };
};

export const createHelpTheme = (parentTheme: Theme, language: string) => {
  const direction = isHelpRtl(language) ? 'rtl' : 'ltr';

  return createTheme(parentTheme, {
    direction,
    typography: withHelpFontFamily(parentTheme, language),
  });
};

export const helpTypographySx = (language: string) => ({
  fontFamily: getHelpFontFamily(language),
  ...helpTextRenderingSx,
  '& .MuiTypography-root, & .MuiButton-root, & .MuiButtonBase-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiCard-root, & .MuiCardContent-root, & .MuiCardActionArea-root, & .MuiPaper-root':
    {
      fontFamily: 'inherit',
      ...helpTextRenderingSx,
    },
});
